/**
 * Display formatting ONLY.
 *
 * The client never performs commercial arithmetic — no sums, no fees, no
 * totals, no derived spend. Everything here takes a number that already exists
 * in the proposal object and turns it into characters. The one scaling that
 * happens is share 0..1 → percent, which is a unit of the same stored value.
 */

const usd0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const usd2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const int = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export function money(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Number.isInteger(value) ? usd0.format(value) : usd2.format(value);
}

export function count(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return int.format(value);
}

/** share is stored 0..1. */
export function percent(share: number | null | undefined, digits = 0): string | null {
  if (share === null || share === undefined || !Number.isFinite(share)) return null;
  return `${(share * 100).toFixed(digits)}%`;
}

export function decimal(value: number | null | undefined, digits = 1): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return value.toFixed(digits);
}

/** 'paid_search' → 'Paid Search'. Used when no catalog name is on hand. */
export function humanizeId(id: string): string {
  return id
    .split(/[_\-.]+/)
    .filter(Boolean)
    .map((word) => {
      const upper = word.toUpperCase();
      if (upper === 'CTV' || upper === 'OTT' || upper === 'PPC' || upper === 'TV') return upper;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A presentation date without day-level precision: "September 2026". */
export function formatMonthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const monthYear = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/**
 * Label for one row of a monthly schedule: "March 2026" when the flight has a
 * start date, otherwise "Month N". Pure date arithmetic (adding whole months
 * to a calendar date) — no commercial values touched.
 */
export function monthLabel(startDate: string | null | undefined, monthIndex: number): string {
  if (!startDate) return `Month ${monthIndex}`;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return `Month ${monthIndex}`;
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + (monthIndex - 1), 1));
  return monthYear.format(target);
}
